import React, { useState, useMemo, useRef, useCallback } from 'react';
import classnames from 'classnames';
import { ResponsiveLine } from '@nivo/line';

import Button from '../components/Button';
import Lazy from '../components/Lazy';
import MultiSelect from '../components/MultiSelect';
import {
	axisRight,
	YearLinesLayer,
	NormalRangeLayer,
	ZScoreHorizontalLayers,
	UncertaintyLayer,
	getToolTip,
	theme,
} from '../components/NivoHelpers';
import RangeSelector from '../components/RangeSelector';
import Section, { Full, Left, Right, Sticky } from '../components/Section';
import Toggle from '../components/Toggle';
import WhatZScore from '../components/WhatZScore';

import { uid, ageGroups, getAgeOptions, downloadFile } from '../utils/data';
import {
	calculateWeekTickValues,
	calculateWeekTickLabels,
	filterByKey,
	sliceGroupsData,
	prettyNumber,
	countGroupsMinMax,
	getPooledByAgeGroupCSV,
} from '../utils/graphs';

import cssVars from '../styles/cssVars';
import shared from '../styles/shared.module.css';
import css from './PooledByAgeGroup.module.css';

export const PooledByAgeGroup = ({ data, ages, printing }) => {
	const { counts, zscores, weeks } = data;
	const id = useMemo(() => uid('pool'), []);

	const ageOptions = useMemo(() => getAgeOptions(ages), [ages]);
	const [selectedAges, setAges] = useState(ageOptions);
	const [range, setRange] = useState({ min: 0, max: weeks.length - 1 });
	const [showZScores, setToggle] = useState(false);
	const [saving, setSaving] = useState(false);
	const graphRef = useRef(null);
	const handleToggle = (n, v) => {
		setToggle(v);
	};

	// Are we showing straight numbers or z-scores?
	const chosenData = showZScores ? zscores : counts;
	const colors = showZScores
		? [cssVars.dataBlue]
		: [cssVars.baselineBlack, cssVars.substantialIncreaseRed, cssVars.dataBlue];

	// Filter by age group
	let filtered = useMemo(
		() =>
			filterByKey(
				chosenData,
				'id',
				selectedAges.map((a) => a.value)
			),
		[selectedAges, chosenData]
	);

	// Filter by week range
	filtered = useMemo(() => sliceGroupsData(filtered, range.min, range.max), [filtered, range]);

	const [minY, maxY, minMaxVals, minX, maxX] = useMemo(() => {
		return countGroupsMinMax(filtered, showZScores);
	}, [filtered, showZScores]);

	// Prepare the tick values and labels
	const xTickLabels = useMemo(() => calculateWeekTickLabels(weeks), [weeks]);
	const xTickValues = useMemo(() => calculateWeekTickValues(filtered, weeks, 10), [filtered, weeks]);

	const onDownloadData = useCallback(
		(filename) => {
			const csv = getPooledByAgeGroupCSV(filtered, weeks);
			downloadFile(filename + '.csv', csv);
		},
		[filtered, weeks]
	);

	const graphs = [];
	for (let i = 0; i < filtered.length; i++) {
		graphs.push(
			<Lazy show={printing || saving} key={`${id}-${i}`} height={filtered[i].id == 'Total' ? 220 + 90 : 180 + 50}>
				<Graph
					item={filtered[i]}
					height={filtered[i].id == 'Total' ? 220 : 180}
					showZScores={showZScores}
					weeks={weeks}
					minY={minMaxVals[i].min}
					maxY={minMaxVals[i].max}
					minX={minX}
					maxX={maxX}
					range={range}
					xTickValues={xTickValues}
					xTickLabels={xTickLabels}
					colors={colors}
					id={id}
				/>
			</Lazy>
		);
	}

	return (
		<Section id={'pooled-by-age-group'} saving={saving}>
			<Full title="Pooled number of deaths by age group">
				<p>
					Graphs showing the pooled weekly total number of deaths in the data-providing EuroMOMO partner
					countries and subnational regions, all ages and by age groups.
				</p>
			</Full>
			<Left
				filename={'charts-pooled-by-age-group'}
				imageRef={graphRef}
				onDownloadData={onDownloadData}
				setSaving={setSaving}
				sticky>
				<MultiSelect
					hideOnPrint
					wideLabels
					className={css.select}
					label="Age groups"
					value={selectedAges}
					options={ageOptions}
					onChange={setAges}
				/>
			</Left>
			<Right forwardRef={graphRef}>
				<Sticky>
					<div className={classnames(shared.row, { [shared.hidden]: saving })}>
						<RangeSelector
							label="Week range"
							className={css.range}
							min={0}
							max={weeks.length - 1}
							value={range}
							onChange={setRange}
							formatLabel={(v) => weeks[v]}
							showYearLabels
							fps={30}
						/>
						<div>
							<Toggle
								className={css.toggle}
								label="Show"
								name={'toggle'}
								value={showZScores}
								onChange={handleToggle}
								innerLabels={['Number', 'Z-scores']}
							/>
							<WhatZScore right />
						</div>
					</div>
					<div className={shared.legend}>
						<div className={shared.item}>
							<span className={shared.line} />
							<h5>Pooled deaths</h5>
						</div>
						<div className={shared.item}>
							<span
								className={shared.line}
								style={{ borderColor: cssVars.normalRangeGray, borderWidth: 15 }}
							/>
							<h5>Normal range</h5>
						</div>
						<div className={shared.item}>
							<span
								className={shared.line}
								style={{
									borderColor: cssVars.baselineBlack,
									borderStyle: 'dashed',
									borderWidth: 1,
								}}
							/>
							<h5>Baseline</h5>
						</div>
						<div className={shared.item}>
							<span
								className={shared.line}
								style={{
									borderColor: cssVars.highlightRed,
									borderStyle: 'dashed',
									borderWidth: 1,
								}}
							/>
							<h5>Substantial increase</h5>
						</div>
						<div className={shared.item}>
							<span
								className={shared.line}
								style={{ borderColor: cssVars.uncertaintyYellow, borderWidth: 15 }}
							/>
							<h5>Corrected for delay in registration</h5>
						</div>
					</div>
				</Sticky>
				<div className={shared.graphsContainer}>{graphs}</div>
			</Right>
		</Section>
	);
};

export default PooledByAgeGroup;

// Setting these outside so nivo caches the numbers
const margin = { top: 10, right: 10, bottom: 18, left: 60 };

const Graph = ({
	item,
	id,
	height,
	showZScores,
	weeks,
	minY,
	maxY,
	minX,
	maxX,
	range,
	xTickValues,
	xTickLabels,
	colors,
}) => {
	const yTicks = item.id === 'Total' ? 6 : 5;
	return (
		<div className={classnames(css.chartContainer, css[`chart${item.id}`])}>
			<h4>{ageGroups[item.id] || item.id}</h4>
			<div
				style={{ height }}
				className={classnames(css.chart, { [css.showNumbers]: !showZScores, [css.showZScores]: showZScores })}>
				<ResponsiveLine
					id={id}
					range={range}
					weeks={weeks}
					data={item.data}
					hnr={item.hnr}
					lnr={item.lnr}
					baseline={item.baseline}
					colors={colors}
					theme={theme}
					margin={margin}
					minY={minY}
					maxY={maxY}
					minX={minX}
					maxX={maxX}
					xScale={{ type: 'linear', min: 'auto', max: 'auto' }}
					yScale={{
						type: 'linear',
						min: minY,
						max: maxY,
					}}
					axisTop={null}
					axisRight={axisRight}
					axisBottom={{
						orient: 'bottom',
						tickSize: 5,
						tickPadding: 0,
						legend: '',
						legendOffset: 56,
						legendPosition: 'middle',
						tickValues: xTickValues,
						format: (weekIdx) => xTickLabels[weekIdx],
					}}
					axisLeft={{
						format: (num) => prettyNumber(num, showZScores, true),
						orient: 'left',
						tickSize: 5,
						tickPadding: 3,
						tickRotation: 0,
						tickValues: yTicks,
						legend: showZScores ? 'Z-Score' : 'Deaths',
						legendOffset: -55,
						legendPosition: 'middle',
					}}
					tooltip={({ point }) => {
						return getToolTip(
							weeks[point.data.x],
							prettyNumber(point.data.yFormatted, showZScores),
							point.serieId
						);
					}}
					enableGridX={false}
					enableGridY={true}
					gridYValues={yTicks}
					lineWidth={2}
					enablePoints={false}
					useMesh={true}
					animate={false}
					layers={[
						'grid',
						'markers',
						'areas',
						YearLinesLayer,
						showZScores ? ZScoreHorizontalLayers : NormalRangeLayer,
						'lines',
						UncertaintyLayer,
						'axes',
						'points',
						'legends',
						'mesh',
						'slices',
					]}
				/>
			</div>
		</div>
	);
};
